import {
  PageSeo,
  breadcrumbsJsonLd,
  faqPageJsonLd,
  organizationJsonLd,
  webApplicationJsonLd,
  webSiteJsonLd,
} from '@imagise/shared';

/**
 * Declarative description of a tool page: the URL slug and the i18n namespace
 * (`TOOL_PAGES.<i18nKey>`, `SEO.<i18nKey>`) holding its texts. Every page
 * renders the full editor — only the heading and SEO content differ.
 */
export interface ToolPageConfig {
  slug: string;
  i18nKey: string;
  stepCount: number;
  faqCount: number;
  /**
   * When set, the editor opens locked to this mode (EditMode enum key):
   * the mode menu is hidden and the tool panel is shown right after upload.
   */
  editMode?: string;
}

export const TOOL_PAGES: ToolPageConfig[] = [
  { slug: 'editor', i18nKey: 'EDITOR', stepCount: 3, faqCount: 3 },
  { slug: 'crop-image', i18nKey: 'CROP', stepCount: 3, faqCount: 3 },
  { slug: 'resize-image', i18nKey: 'RESIZE', stepCount: 3, faqCount: 3 },
  { slug: 'flip-rotate', i18nKey: 'ROTATE', stepCount: 3, faqCount: 3 },
  { slug: 'image-filters', i18nKey: 'FILTERS', stepCount: 3, faqCount: 3 },
  { slug: 'add-watermark', i18nKey: 'WATERMARK', stepCount: 3, faqCount: 3 },
  { slug: 'deskew-image', i18nKey: 'DESKEW', stepCount: 3, faqCount: 3 },
  { slug: 'animation-maker', i18nKey: 'ANIMATION', stepCount: 3, faqCount: 3, editMode: 'Animation' },
  { slug: 'merge-images', i18nKey: 'MERGE', stepCount: 3, faqCount: 3, editMode: 'Merge' },
  { slug: 'object-detection', i18nKey: 'OBJECT_DETECTION', stepCount: 3, faqCount: 3, editMode: 'ObjectDetection' },
];

export function toolStepKeys(tool: ToolPageConfig): { titleKey: string; contentKey: string }[] {
  return Array.from({ length: tool.stepCount }, (_, index) => ({
    titleKey: `TOOL_PAGES.${tool.i18nKey}.STEPS.S${index + 1}.TITLE`,
    contentKey: `TOOL_PAGES.${tool.i18nKey}.STEPS.S${index + 1}.CONTENT`,
  }));
}

export function toolFaqKeys(tool: ToolPageConfig): { questionKey: string; answerKey: string }[] {
  return Array.from({ length: tool.faqCount }, (_, index) => ({
    questionKey: `TOOL_PAGES.${tool.i18nKey}.FAQ.Q${index + 1}.QUESTION`,
    answerKey: `TOOL_PAGES.${tool.i18nKey}.FAQ.Q${index + 1}.ANSWER`,
  }));
}

export function toolPageSeo(tool: ToolPageConfig): PageSeo {
  return {
    titleKey: `SEO.${tool.i18nKey}.TITLE`,
    descriptionKey: `SEO.${tool.i18nKey}.DESCRIPTION`,
    keywordsKey: `SEO.${tool.i18nKey}.KEYWORDS`,
    path: tool.slug,
    jsonLd: [
      webApplicationJsonLd({
        nameKey: `TOOL_PAGES.${tool.i18nKey}.NAME`,
        descriptionKey: `SEO.${tool.i18nKey}.DESCRIPTION`,
      }),
      breadcrumbsJsonLd([
        { nameKey: 'BREADCRUMBS.HOME', path: '' },
        { nameKey: `TOOL_PAGES.${tool.i18nKey}.NAME`, path: tool.slug },
      ]),
      faqPageJsonLd(toolFaqKeys(tool)),
    ],
  };
}

export const HOME_FAQ_COUNT = 5;

export function homeFaqKeys(): { questionKey: string; answerKey: string }[] {
  return Array.from({ length: HOME_FAQ_COUNT }, (_, index) => ({
    questionKey: `HOME.FAQ.Q${index + 1}.QUESTION`,
    answerKey: `HOME.FAQ.Q${index + 1}.ANSWER`,
  }));
}

export const HOME_PAGE_SEO: PageSeo = {
  titleKey: 'SEO.HOME.TITLE',
  descriptionKey: 'SEO.HOME.DESCRIPTION',
  keywordsKey: 'SEO.HOME.KEYWORDS',
  path: '',
  jsonLd: [
    webSiteJsonLd(),
    organizationJsonLd(),
    webApplicationJsonLd({
      nameKey: 'SEO.HOME.APP_NAME',
      descriptionKey: 'SEO.HOME.DESCRIPTION',
      featureKeys: TOOL_PAGES.map(tool => `TOOL_PAGES.${tool.i18nKey}.NAME`),
    }),
    faqPageJsonLd(homeFaqKeys()),
  ],
};
